import { getSessionAuthentication, baseUrl } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
    locale?: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface PrintDocumentTemplate {
    id: number;
    description: string;
}

export interface DeliveryPlanningTemplate {
    id: number;
    deliveryPlanningTemplateName: string;
}

export interface OrderType {
    id: number;
    orderTypeName: string;
    printDocumentTemplate: PrintDocumentTemplate | null;
    pickingListDocumentTemplate: PrintDocumentTemplate | null;
    deliveryPlanningTemplate: DeliveryPlanningTemplate | null;
    deliveryPlanningTemplateId?: number | null;
    localizations: Localization[];
}

export async function getOrderType(id: number): Promise<OrderType> {
    return legacyFetch<OrderType>(`ordertypes/${id}`);
}

export async function createOrderType(ot: Partial<OrderType>): Promise<OrderType> {
    return legacyFetch<OrderType>('ordertypes', {
        method: 'POST',
        body: JSON.stringify(ot),
    });
}

export async function updateOrderType(ot: OrderType): Promise<OrderType> {
    return legacyFetch<OrderType>(`ordertypes/${ot.id}`, {
        method: 'PUT',
        body: JSON.stringify(ot),
    });
}

export async function deleteOrderType(id: number): Promise<void> {
    await legacyFetch<void>(`ordertypes/${id}`, { method: 'DELETE' });
}

export async function getLanguages(): Promise<Language[]> {
    return legacyFetch<Language[]>('ordertypes/languages');
}

export async function getPrintDocumentTemplates(): Promise<PrintDocumentTemplate[]> {
    return legacyFetch<PrintDocumentTemplate[]>('ordertypes/printDocumentTemplates');
}

export async function getDeliveryPlanningTemplates(): Promise<{ deliveryPlanningTemplates: DeliveryPlanningTemplate[] }> {
    return legacyFetch<{ deliveryPlanningTemplates: DeliveryPlanningTemplate[] }>('ordertypes/deliveryPlanningTemplates');
}

export async function isDeliveryPlanningEnabled(): Promise<boolean> {
    return legacyFetch<boolean>('orders/isdeliveryplanningenabled');
}
